/**
 * Epsilon for "are these two budget values effectively equal?" comparisons in
 * the Allocatable Budget flow. Half a cent — well below the BudgetBar
 * `<input>`'s `step="0.01"` and well above any realistic floating-point noise
 * from cent rounding.
 *
 * It is load-bearing that this is strictly less than the input step: a single
 * spinner click moves the value by exactly the step, and if the epsilon
 * equalled the step the smart-clear in the auto-sync effect would treat the
 * post-click value as "still aligned" and revert it.
 */
export const BUDGET_ALIGNMENT_EPSILON = 0.005;

/**
 * Returns true when `a` and `b` represent the same budget at cent precision.
 *
 * Inputs are rounded to cents internally, so callers don't need to pre-round
 * (this matches the function's domain — currency at cent precision — and means
 * `isBudgetAligned(100.006, 100)` and `isBudgetAligned(100.01, 100)` agree, as
 * both round to the same input pair).
 */
export function isBudgetAligned(a: number, b: number): boolean {
  const aRounded = Math.round(a * 100) / 100;
  const bRounded = Math.round(b * 100) / 100;
  return Math.abs(aRounded - bRounded) < BUDGET_ALIGNMENT_EPSILON;
}
